import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, History, UserCog } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateShort } from "@/lib/followupConstants";
import { FOLLOWUPS } from "@/constants/testIds";
import FollowUpBadge from "@/components/followups/FollowUpBadge";
import FollowUpActionButtons from "@/components/followups/FollowUpActionButtons";
import FollowUpActionModal from "@/components/followups/FollowUpActionModal";
import FollowUpHistoryModal from "@/components/followups/FollowUpHistoryModal";
import FollowUpWhatsAppModal from "@/components/followups/FollowUpWhatsAppModal";

const PATIENT_TEST_IDS = {
  contact: FOLLOWUPS.contactButtonPrefix,
  whatsapp: FOLLOWUPS.whatsappButtonPrefix,
  postpone: FOLLOWUPS.postponeButtonPrefix,
  discard: FOLLOWUPS.discardButtonPrefix,
  reactivate: FOLLOWUPS.reactivateButtonPrefix,
};

/** Panel de Seguimiento y recurrencia: motor de recurrencia que convierte el
 * historial de tratamientos en una lista accionable de pacientes que deben
 * volver (vencidas/por vencer/en riesgo). Admin/Recepcion pueden actuar;
 * Profesional solo visualiza a sus propios pacientes (RBAC del backend). */
export default function FollowUpsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canAct = user?.role === "admin" || user?.role === "reception";
  const showProfessionalFilter = user?.role !== "professional";

  const [bucket, setBucket] = useState("all");
  const [contactStatus, setContactStatus] = useState("active");
  const [serviceId, setServiceId] = useState("all");
  const [professionalId, setProfessionalId] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ mode: null, treatment: null });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [whatsappTarget, setWhatsappTarget] = useState(null);
  const [clinic, setClinic] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/team/professionals/list"), api.get("/clinic")])
      .then(([s, p, c]) => { setServices(s.data); setProfessionals(p.data); setClinic(c.data); })
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { bucket, contact_status: contactStatus };
      if (serviceId !== "all") params.service_id = serviceId;
      if (showProfessionalFilter && professionalId !== "all") params.professional_id = professionalId;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/followups", { params });
      setRows(data);
    } catch (err) {
      toast({ title: "Error al cargar el seguimiento", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, contactStatus, serviceId, professionalId, search]);

  const handleActionSuccess = () => {
    toast({ title: "Seguimiento actualizado" });
    load();
  };

  const handleReactivate = async (treatment) => {
    try {
      await api.post(`/followups/${treatment.id}/reactivate`);
      toast({ title: "Seguimiento reactivado" });
      load();
    } catch (err) {
      toast({ title: "Error al reactivar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid={FOLLOWUPS.page}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Seguimiento</h1>
        <p className="text-base text-muted-foreground mt-1">
          Pacientes que ya deberian volver segun la duracion del efecto de su ultimo tratamiento.
        </p>
      </div>

      <Tabs value={bucket} onValueChange={setBucket}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid={FOLLOWUPS.bucketTabAll}>Todas</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid={FOLLOWUPS.bucketTabUpcoming}>Por vencer</TabsTrigger>
          <TabsTrigger value="overdue" data-testid={FOLLOWUPS.bucketTabOverdue}>Vencidas</TabsTrigger>
          <TabsTrigger value="at_risk" data-testid={FOLLOWUPS.bucketTabAtRisk}>En riesgo</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex items-center gap-3 flex-wrap">
        <Input
          className="flex-1 min-w-[200px] max-w-sm"
          placeholder="Buscar paciente por nombre o telefono..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid={FOLLOWUPS.searchInput}
        />
        <Select value={contactStatus} onValueChange={setContactStatus}>
          <SelectTrigger className="w-44" data-testid={FOLLOWUPS.contactStatusSelect}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="discarded">Descartados</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={serviceId} onValueChange={setServiceId}>
          <SelectTrigger className="w-52" data-testid={FOLLOWUPS.serviceFilterSelect}><SelectValue placeholder="Servicio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los servicios</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {showProfessionalFilter && (
          <Select value={professionalId} onValueChange={setProfessionalId}>
            <SelectTrigger className="w-52" data-testid={FOLLOWUPS.professionalFilterSelect}><SelectValue placeholder="Profesional" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los profesionales</SelectItem>
              {professionals.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Cargando seguimiento...</p>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid={FOLLOWUPS.emptyState}>
          <CalendarClock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No hay pacientes en este filtro.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid={FOLLOWUPS.list}>
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-border rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`${FOLLOWUPS.rowPrefix}${r.id}`}>
              <div className="min-w-[220px]">
                <button
                  className="text-sm font-medium text-foreground hover:text-brand hover:underline text-left"
                  onClick={() => navigate(`/pacientes/${r.patient_id}`)}
                  data-testid={`${FOLLOWUPS.viewProfileButtonPrefix}${r.id}`}
                >
                  {r.patient_name}
                </button>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {r.service_name || "Tratamiento"} &middot; {formatDateShort(r.treatment_date)}
                </p>
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-0.5">
                  <UserCog className="w-3 h-3" /> {r.professional_name}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <FollowUpBadge bucket={r.bucket} daysOverdue={r.days_overdue} followUpStatus={r.follow_up_status} />
                <p className="text-xs text-muted-foreground">Vuelve: {formatDateShort(r.next_due_date)}</p>
              </div>

              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setHistoryTarget(r)} data-testid={`${FOLLOWUPS.historyButtonPrefix}${r.id}`}>
                  <History className="w-3.5 h-3.5" />
                </Button>
                {canAct && (
                  <FollowUpActionButtons
                    treatment={r}
                    onContact={(t) => setModalState({ mode: "contact", treatment: t })}
                    onWhatsApp={(t) => setWhatsappTarget(t)}
                    onPostpone={(t) => setModalState({ mode: "postpone", treatment: t })}
                    onDiscard={(t) => setModalState({ mode: "discard", treatment: t })}
                    onReactivate={handleReactivate}
                    testIds={PATIENT_TEST_IDS}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <FollowUpActionModal
        open={Boolean(modalState.mode)}
        onOpenChange={(open) => !open && setModalState({ mode: null, treatment: null })}
        mode={modalState.mode}
        treatment={modalState.treatment}
        onSuccess={handleActionSuccess}
      />

      <FollowUpHistoryModal
        open={Boolean(historyTarget)}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        patientId={historyTarget?.patient_id}
        patientName={historyTarget?.patient_name}
      />

      <FollowUpWhatsAppModal
        open={Boolean(whatsappTarget)}
        onOpenChange={(open) => !open && setWhatsappTarget(null)}
        treatment={whatsappTarget}
        template={clinic?.followup_whatsapp_template}
        clinicName={clinic?.name}
        onSuccess={handleActionSuccess}
      />
    </div>
  );
}
